import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Printer, FilePdf, FileCsv, EnvelopeSimple, ArrowsClockwise, Warning, CheckCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API, authHeaders, getToken } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";

const PRESETS = [
    { id: "today", label: "Hoy" },
    { id: "yesterday", label: "Ayer" },
    { id: "week", label: "Esta semana" },
    { id: "month", label: "Este mes" },
    { id: "custom", label: "Rango" },
];
const CHANNELS = [
    { id: "all", label: "Todos" },
    { id: "pos", label: "En tienda" },
    { id: "online", label: "Online" },
];

const money = (v) => (v === null || v === undefined ? "n/a" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const cap = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function SalesReport() {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    const [preset, setPreset] = useState("today");
    const [dateFrom, setDateFrom] = useState(today);
    const [dateTo, setDateTo] = useState(today);
    const [timeFrom, setTimeFrom] = useState("");
    const [timeTo, setTimeTo] = useState("");
    const [channel, setChannel] = useState("all");
    const [terminal, setTerminal] = useState("all");
    const [terminals, setTerminals] = useState([]);
    const [rep, setRep] = useState(null);
    const [loading, setLoading] = useState(false);

    const params = useMemo(() => {
        const p = new URLSearchParams({ preset, channel });
        if (preset === "custom") {
            p.set("date_from", dateFrom);
            p.set("date_to", dateTo || dateFrom);
            if (timeFrom) p.set("time_from", timeFrom);
            if (timeTo) p.set("time_to", timeTo);
        }
        if (terminal !== "all") p.set("terminal", terminal);
        return p.toString();
    }, [preset, channel, dateFrom, dateTo, timeFrom, timeTo, terminal]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/sales-report?${params}`, { headers: authHeaders() });
            setRep(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo generar el reporte");
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/admin/sales-report/terminals", { headers: authHeaders() })
            .then((r) => setTerminals(r.data.terminals || [])).catch(() => {});
    }, []);

    const exportUrl = (fmt, extra = "") => `${API}/admin/sales-report/export?fmt=${fmt}&${params}&token=${getToken()}${extra}`;

    const printThermal = async () => {
        const w = Number(localStorage.getItem("jw_printer_width")) || 58;
        const cols = w >= 80 ? 48 : 32;
        try {
            const r = await fetch(exportUrl("txt", `&width=${cols}`));
            const text = await r.text();
            const win = window.open("", "_blank", "width=420,height=720");
            if (!win) { toast.error("Permite ventanas emergentes para imprimir"); return; }
            win.document.write(`<!doctype html><html><head><title>Sales Report</title><style>
                @page{margin:4mm;size:${w}mm auto}
                body{margin:0;font-family:"Courier New",monospace;font-size:${w >= 80 ? 11 : 10}px;line-height:1.25}
                pre{white-space:pre;margin:0}
            </style></head><body><pre>${text.replace(/</g, "&lt;")}</pre></body></html>`);
            win.document.close();
            win.focus();
            setTimeout(() => win.print(), 300);
        } catch {
            toast.error("No se pudo preparar la impresión");
        }
    };

    const emailReport = () => {
        if (!rep) return;
        const h = rep.header, ns = rep.net_sales, p = rep.payments;
        const subject = encodeURIComponent(`J&W Deli — Sales Report ${h.start_date}${h.end_date !== h.start_date ? ` → ${h.end_date}` : ""}`);
        const body = encodeURIComponent(
            `J&W DELI — SALES REPORT\nFrom: ${h.start_date} ${h.start_time}\nTo: ${h.end_date} ${h.end_time}\nChannel: ${h.channel} · Terminal: ${h.terminal}\n\n` +
            `Gross Sales: ${money(rep.sales.total_gross_sales)}\nIn-Store Gross: ${money(rep.sales.in_store_gross_sales)}\nOnline Gross: ${money(rep.sales.online_gross_sales)}\n\n` +
            `Cash: ${money(p.cash)}\nCredit/Debit: ${money(p.credit_debit)}\nOnline Card: ${money(p.online_card)}\nTotal Payments: ${money(p.total)}\n\n` +
            `Sales Tax: ${money(rep.taxes_fees.sales_tax)}\nTips: ${money(rep.taxes_fees.tips)}\nDelivery Fees: ${money(rep.taxes_fees.delivery_fees)}\n` +
            `Discounts: ${money(ns.discounts)}\nRefunds: ${money(ns.refunds)}\n\n` +
            `IN-STORE NET SALES: ${money(ns.in_store_net_sales)}\nONLINE NET SALES: ${money(ns.online_net_sales)}\nTOTAL NET SALES: ${money(ns.total_net_sales)}\n\n` +
            `Transactions: ${rep.channels.total_transactions} · Avg ticket: ${money(rep.channels.overall_average_ticket)}\n\n(El PDF completo se descargó — adjúntalo a este correo.)`,
        );
        window.open(exportUrl("pdf"), "_blank");
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    const pill = (active) => `px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-widest border-2 transition ${
        active ? "bg-foreground text-background border-foreground" : "bg-white text-foreground/70 border-border hover:border-foreground"}`;

    return (
        <div data-testid="admin-sales-report">
            <PageHeader
                kicker="Cierre POS + Online"
                title="Reporte de Ventas"
                action={
                    <div className="flex flex-wrap gap-2" data-testid="sales-report-actions">
                        <Button data-testid="sr-print-btn" onClick={printThermal} variant="outline" className="rounded-full h-10 font-bold uppercase tracking-widest text-[11px]">
                            <Printer size={16} weight="bold" className="mr-1.5" /> Print
                        </Button>
                        <a data-testid="sr-pdf-btn" href={exportUrl("pdf")} target="_blank" rel="noreferrer"
                           className="inline-flex items-center h-10 px-4 rounded-full border-2 border-border bg-white hover:border-foreground font-bold uppercase tracking-widest text-[11px]">
                            <FilePdf size={16} weight="bold" className="mr-1.5" /> Download PDF
                        </a>
                        <a data-testid="sr-csv-btn" href={exportUrl("csv")} target="_blank" rel="noreferrer"
                           className="inline-flex items-center h-10 px-4 rounded-full border-2 border-border bg-white hover:border-foreground font-bold uppercase tracking-widest text-[11px]">
                            <FileCsv size={16} weight="bold" className="mr-1.5" /> Export CSV
                        </a>
                        <Button data-testid="sr-email-btn" onClick={emailReport} className="rounded-full h-10 bg-foreground hover:bg-foreground/90 text-background font-bold uppercase tracking-widest text-[11px]">
                            <EnvelopeSimple size={16} weight="bold" className="mr-1.5" /> Email Report
                        </Button>
                    </div>
                }
            />

            {/* Filters */}
            <div data-testid="sr-filters" className="rounded-2xl border border-border bg-muted/40 p-4 mb-6 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground w-16">Periodo</span>
                    {PRESETS.map((p) => (
                        <button key={p.id} data-testid={`sr-preset-${p.id}`} onClick={() => setPreset(p.id)} className={pill(preset === p.id)}>{p.label}</button>
                    ))}
                    {preset === "custom" && (
                        <div className="flex flex-wrap items-center gap-2 ml-2" data-testid="sr-custom-range">
                            <Input data-testid="sr-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40 bg-white" />
                            <Input data-testid="sr-time-from" type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} className="h-9 w-28 bg-white" />
                            <span className="text-xs text-muted-foreground">→</span>
                            <Input data-testid="sr-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40 bg-white" />
                            <Input data-testid="sr-time-to" type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} className="h-9 w-28 bg-white" />
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground w-16">Canal</span>
                    {CHANNELS.map((c) => (
                        <button key={c.id} data-testid={`sr-channel-${c.id}`} onClick={() => setChannel(c.id)} className={pill(channel === c.id)}>{c.label}</button>
                    ))}
                    <span className="h-5 w-px bg-border mx-1 hidden sm:block" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Terminal</span>
                    <select data-testid="sr-terminal-select" value={terminal} onChange={(e) => setTerminal(e.target.value)}
                            className="h-9 rounded-full border-2 border-border bg-white px-3 text-xs font-bold focus:outline-none focus:border-foreground">
                        <option value="all">Todas</option>
                        {terminals.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button data-testid="sr-refresh-btn" onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-primary hover:underline">
                        <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} /> Actualizar
                    </button>
                </div>
            </div>

            {rep && <ReportBody rep={rep} />}
        </div>
    );
}

function ReportBody({ rep }) {
    const { header: h, sales: s, payments: p, online: o, in_store: i, adjustments, taxes_fees: tf, categories: cat, channels: ch, net_sales: ns, reconciliation: rc } = rep;
    const hasDiff = Math.abs(rc.difference) > 0.004 || Math.abs(rc.cash_difference) > 0.004;
    return (
        <div className="space-y-6">
            {/* Header */}
            <div data-testid="sr-header" className="rounded-2xl bg-foreground text-background p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2 md:col-span-4">
                    <p className="text-[10px] uppercase tracking-[0.3em] opacity-70">{h.business}</p>
                    <p className="font-display font-black text-2xl">{h.title}</p>
                </div>
                <Meta k="Fecha / hora inicial" v={`${h.start_date} ${h.start_time}`} testid="sr-start" />
                <Meta k="Fecha / hora final" v={`${h.end_date} ${h.end_time}`} testid="sr-end" />
                <Meta k="Canal · Terminal" v={`${h.channel.toUpperCase()} · ${h.terminal}`} />
                <Meta k="Usuario / cajero" v={h.generated_by} />
                <Meta k="Generado" v={new Date(h.generated_at).toLocaleString("en-US", { timeZone: h.timezone })} testid="sr-generated" />
                <Meta k="Cajeros en periodo" v={h.cashiers_in_range.join(", ") || "—"} />
                <Meta k="Terminales en periodo" v={h.terminals_in_range.join(", ") || "—"} />
                <Meta k="Tasa vigente" v={`${(h.tax_rate * 100).toFixed(2)}%`} />
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="sr-kpis">
                <Kpi label="Total Net Sales" value={money(ns.total_net_sales)} testid="kpi-net" accent />
                <Kpi label="Gross Sales" value={money(s.total_gross_sales)} testid="kpi-gross" />
                <Kpi label="Cash" value={money(p.cash)} testid="kpi-cash" />
                <Kpi label="Card (in-store)" value={money(p.credit_debit)} testid="kpi-card" />
                <Kpi label="Online Card" value={money(p.online_card)} testid="kpi-online" />
                <Kpi label="Transactions" value={ch.total_transactions} testid="kpi-tx" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <Section title="1 · Sales / Ventas" testid="sec-sales">
                    <R k="Taxable Product Sales" v={s.taxable_product_sales} />
                    <R k="Taxable Other Sales" v={s.taxable_other_sales} />
                    <R k="Total Tax and Fees" v={s.total_tax_and_fees} />
                    <R k="Non-Taxable Product Sales" v={s.non_taxable_product_sales} />
                    <R k="Non-Taxable Other Sales" v={s.non_taxable_other_sales} />
                    <R k="Gross Sales" v={s.gross_sales} sub />
                    <R k="In-Store Gross Sales" v={s.in_store_gross_sales} />
                    <R k="Online Gross Sales" v={s.online_gross_sales} />
                    <R k="TOTAL GROSS SALES" v={s.total_gross_sales} total testid="sr-total-gross" />
                </Section>

                <Section title="2 · Payment Methods / Métodos de pago" testid="sec-payments">
                    <R k="Cash" v={p.cash} n={p.counts.cash} />
                    <R k="Check" v={p.check} n={p.counts.check} />
                    <R k="Credit/Debit" v={p.credit_debit} n={p.counts.credit_debit} />
                    <R k="Online Card Payments" v={p.online_card} n={p.counts.online_card} />
                    <R k="Other Payments" v={p.other} n={p.counts.other} />
                    <R k="TOTAL PAYMENTS" v={p.total} total testid="sr-total-payments" />
                </Section>

                <Section title="3 · Online Sales Summary" testid="sec-online">
                    <R k="Online Orders" v={o.orders} raw />
                    <R k="Online Product Sales" v={o.product_sales} />
                    <R k="Online Tax" v={o.tax} />
                    <R k="Online Delivery Fees" v={o.delivery_fees} />
                    <R k="Online Tips" v={o.tips} />
                    <R k="Online Discounts" v={o.discounts} />
                    <R k="Online Refunds" v={o.refunds} />
                    <R k="Online Gross Sales" v={o.gross_sales} sub />
                    <R k="Online Net Sales" v={o.net_sales} total testid="sr-online-net" />
                    <R k="Average Online Order" v={o.average_order} />
                    <R k="Number of Online Orders" v={o.orders} raw />
                    <R k="Cancelled Online Orders" v={o.cancelled_orders} raw />
                    <R k="Refunded Online Orders" v={o.refunded_orders} raw />
                </Section>

                <Section title="4 · In-Store Sales Summary" testid="sec-instore">
                    <R k="Number of Transactions" v={i.transactions} raw />
                    <R k="Product Sales" v={i.product_sales} />
                    <R k="Tax" v={i.tax} />
                    <R k="Tips" v={i.tips} />
                    <R k="Discounts" v={i.discounts} />
                    <R k="Refunds" v={i.refunds} />
                    <R k="Gross Sales" v={i.gross_sales} sub />
                    <R k="Net Sales" v={i.net_sales} total testid="sr-instore-net" />
                    <R k="Average Ticket" v={i.average_ticket} />
                </Section>

                <Section title="5 · Refunds / Discounts / Voids" testid="sec-adjustments" head={["", "COUNT", "AMOUNT"]}>
                    {adjustments.map((a) => <R key={a.key} k={a.label} n={a.count} v={a.amount} testid={`adj-${a.key}`} />)}
                </Section>

                <Section title="6 · Taxes and Fees Collection Summary" testid="sec-taxes">
                    <R k="Taxable Sales" v={tf.taxable_sales} />
                    <R k={`Sales Tax ${tf.rate_label}`} v={tf.sales_tax} testid="sr-sales-tax" />
                    <R k="Non-Taxable Sales" v={tf.non_taxable_sales} />
                    {tf.other_taxes > 0 && <R k="Other Taxes" v={tf.other_taxes} />}
                    <R k="Service Fees" v={tf.service_fees} />
                    <R k="Delivery Fees" v={tf.delivery_fees} />
                    <R k="Tips" v={tf.tips} />
                    <R k="TOTAL TAXES AND FEES" v={tf.total} total />
                </Section>

                <Section title="7 · Net Product Sales by Category" testid="sec-categories" head={["CATEGORY", "QTY", "NET SALES"]}>
                    {cat.rows.length === 0 && <p className="text-xs text-muted-foreground py-2">Sin ventas en el periodo</p>}
                    {cat.rows.map((c) => <R key={c.category} k={cap(c.category)} n={c.qty} v={c.net_sales} testid={`cat-${c.category}`} />)}
                    <R k="TOTAL" n={cat.total_qty} v={cat.total_net} total testid="sr-cat-total" />
                </Section>

                <Section title="8 · Sales by Channel" testid="sec-channels">
                    <R k="In-Store Sales" v={ch.in_store_sales} />
                    <R k="Online Sales" v={ch.online_sales} />
                    <R k="Combined Sales" v={ch.combined_sales} sub />
                    <R k="In-Store Transactions" v={ch.in_store_transactions} raw />
                    <R k="Online Orders" v={ch.online_orders} raw />
                    <R k="Total Transactions" v={ch.total_transactions} raw />
                    <R k="In-Store Average Ticket" v={ch.in_store_average_ticket} />
                    <R k="Online Average Ticket" v={ch.online_average_ticket} />
                    <R k="Overall Average Ticket" v={ch.overall_average_ticket} total />
                </Section>

                <Section title="9 · Net Sales" testid="sec-net">
                    <R k="Gross Sales" v={ns.gross_sales} />
                    <R k="− Discounts" v={ns.discounts} />
                    <R k="− Refunds" v={ns.refunds} />
                    <R k="= Net Product Sales" v={ns.net_product_sales} sub />
                    <R k="+ Other Sales" v={ns.other_sales} />
                    <R k="+ Applicable Fees" v={ns.applicable_fees} />
                    <R k="IN-STORE NET SALES" v={ns.in_store_net_sales} sub />
                    <R k="ONLINE NET SALES" v={ns.online_net_sales} sub />
                    <R k="TOTAL NET SALES" v={ns.total_net_sales} total testid="sr-total-net" />
                </Section>

                <Section title="10 · Reconciliación de pagos" testid="sec-reconciliation"
                         badge={hasDiff
                             ? <span data-testid="sr-diff-alert" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-red-700 bg-red-100 px-2.5 py-1 rounded-full"><Warning size={12} weight="fill" /> Diferencia</span>
                             : <span data-testid="sr-diff-ok" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full"><CheckCircle size={12} weight="fill" /> Cuadra</span>}>
                    <R k="Expected Cash" v={rc.expected_cash} />
                    <R k="Actual Cash" v={rc.actual_cash} hint={rc.actual_cash === null ? "sin cierre de caja" : undefined} />
                    <R k="Cash Difference" v={rc.cash_difference} warn={Math.abs(rc.cash_difference) > 0.004} />
                    <R k="Card Sales" v={rc.card_sales} />
                    <R k="Online Card Sales" v={rc.online_card_sales} />
                    <R k="Refunds" v={rc.refunds} />
                    <R k="Expected Total" v={rc.expected_total} />
                    <R k="Recorded Total" v={rc.recorded_total} />
                    <R k="DIFFERENCE" v={rc.difference} total warn={Math.abs(rc.difference) > 0.004} testid="sr-difference" />
                </Section>
            </div>
        </div>
    );
}

function Meta({ k, v, testid }) {
    return (
        <div data-testid={testid}>
            <p className="text-[10px] uppercase tracking-widest opacity-60">{k}</p>
            <p className="font-bold text-sm mt-0.5 break-words">{v}</p>
        </div>
    );
}

function Kpi({ label, value, testid, accent }) {
    return (
        <div data-testid={testid} className={`rounded-2xl border p-4 ${accent ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border"}`}>
            <p className={`text-[10px] font-bold uppercase tracking-widest ${accent ? "opacity-80" : "text-muted-foreground"}`}>{label}</p>
            <p className="font-display font-black text-2xl mt-1 tabular-nums">{value}</p>
        </div>
    );
}

function Section({ title, children, testid, head, badge }) {
    return (
        <section data-testid={testid} className="rounded-2xl border border-border bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-muted/50 border-b border-border">
                <h2 className="font-display font-black text-base tracking-tight">{title}</h2>
                {badge}
            </div>
            <div className="px-5 py-3 font-mono text-[13px]">
                {head && (
                    <div className="grid grid-cols-[1fr,64px,110px] text-[10px] font-bold uppercase tracking-widest text-muted-foreground pb-1 border-b border-dashed border-border">
                        <span>{head[0]}</span><span className="text-right">{head[1]}</span><span className="text-right">{head[2]}</span>
                    </div>
                )}
                {children}
            </div>
        </section>
    );
}

function R({ k, v, n, raw, sub, total, warn, hint, testid }) {
    const val = raw ? String(v) : money(v);
    return (
        <div data-testid={testid}
             className={`grid ${n !== undefined ? "grid-cols-[1fr,64px,110px]" : "grid-cols-[1fr,110px]"} items-baseline py-1 tabular-nums ${
                 total ? "border-t-2 border-foreground mt-1 pt-2 font-black text-sm" : sub ? "border-t border-dashed border-border mt-1 pt-1.5 font-bold" : ""
             } ${warn ? "text-red-600" : ""}`}>
            <span className={`truncate ${total ? "uppercase" : "text-foreground/80"}`}>
                {k}{hint && <span className="ml-2 text-[10px] text-muted-foreground normal-case">({hint})</span>}
            </span>
            {n !== undefined && <span className="text-right text-muted-foreground">{n}</span>}
            <span className="text-right">{val}</span>
        </div>
    );
}
